import React, { useCallback, useEffect, useState } from 'react';
import {
  Tooltip, Box, Typography, Avatar, Dialog, DialogTitle, DialogContent, DialogActions,
  Button, IconButton, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, TablePagination, Collapse, useMediaQuery, useTheme,
} from '@mui/material';
import { Card, CardContent } from '../../../../../components/tailwind-component/ui/card';
import {
  Zap, Send, MailOpen, MousePointerClick, TrendingDown, RefreshCw,
  ChevronRight, ChevronDown, ExternalLink, Package, Info,
  Smartphone, Monitor, Tablet, HelpCircle, MessageSquare, BookOpen, Radio,
  LayoutGrid, ListChecks,
  Reply,  TrendingUp,
  IndianRupee,
  ShoppingBag,
  Clock3,
} from 'lucide-react';
import { InstagramDMAutomation } from '../../../../../core/services/dm-automation-instagram.service';
import * as gtUtil from '../../../../../core/utility';
import { BASE_URL } from '../../../../../config/config';
import {
  InfluencerZapTotals,
  InfluencerZapAutomationCounts,
  InfluencerZapAutomationStat,
  InfluencerZapProductRow,
  InfluencerZapEventTypeRow,
  InfluencerZapDeviceRow,
  InfluencerZapActivityRow,
  InfluencerZapRuleRow,
  InfluencerZapPostRow,
  InfluencerZapAutomationMeta,
  InfluencerZapClickDetail,
} from 'custom-type';
import { Tabs, Tab } from '@mui/material';
import { Link } from 'react-router-dom';
import zapIconActive from '../../../../../assets/images/zapIconActive.svg';

// One automation's full detail, used by the "Automations" modal (mirrors the
// Admin dashboard's per-automation card: meta + linked posts + rules + activity).
type AutoDetail = {
  stat: InfluencerZapAutomationStat;
  meta: InfluencerZapAutomationMeta | null;
  rules: InfluencerZapRuleRow[];
  posts: InfluencerZapPostRow[];
  activity: InfluencerZapActivityRow[];
};

const instagramDMAutomationService: InstagramDMAutomation = new InstagramDMAutomation();

const today = new Date().toISOString().split('T')[0];
const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];

const fmtNum = (n: number | string | null | undefined) => Number(n || 0).toLocaleString();
const pct = (num: number, den: number) => (!den ? '—' : ((num / den) * 100).toFixed(1) + '%');
const fmtDateTime = (v: string | null | undefined) => {
  if (!v) return '—';
  const d = new Date(v.endsWith('Z') ? v : v + 'Z');
  return d.toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit', hour12: true });
};

const EMPTY_TOTALS: InfluencerZapTotals = {
  total_triggers: 0, sent_triggers: 0, failed_triggers: 0, pending_triggers: 0,
  opened_triggers: 0, total_links_sent: 0, total_clicks: 0, unique_clicks: 0,
};
const EMPTY_COUNTS: InfluencerZapAutomationCounts = {
  total_automations: 0, post_automations: 0, story_automations: 0, live_automations: 0,
  total_rules: 0, auto_reply_rules: 0, zap_link_rules: 0,
  rules_with_comment_reply: 0, rules_with_dm_text: 0, rules_with_zap_links: 0,
};

const EVENT_TYPE_LABEL: Record<string, string> = {
  comments: 'Comments', story_replies: 'Story Replies', live_comments: 'Live Comments',
};
const DEVICE_META: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  mobile:  { label: 'Mobile',  color: '#26a69a', icon: <Smartphone className="w-4 h-4" /> },
  desktop: { label: 'Desktop', color: '#5c6bc0', icon: <Monitor className="w-4 h-4" /> },
  tablet:  { label: 'Tablet',  color: '#ffa726', icon: <Tablet className="w-4 h-4" /> },
  unknown: { label: 'Unknown', color: '#9e9e9e', icon: <HelpCircle className="w-4 h-4" /> },
};

const shortLinkUrl = (code: string | null | undefined) => (code ? `${BASE_URL}/zap/${code}` : '');
const productImg = (p: { manual_product_image_url?: string | null; product_image_url?: string | null }) =>
  p.manual_product_image_url || p.product_image_url || '';
// Best-available thumbnail for the post/reel/story the comment was on (prefers our GCP copy)
const postThumb = (p: { post_gcp_url?: string | null; post_thumbnail_url?: string | null; post_media_url?: string | null }) =>
  p.post_gcp_url || p.post_thumbnail_url || p.post_media_url || '';
const igProfileUrl = (username: string | null | undefined) => {
  if (!username) return '';
  const cleaned = String(username).replace(/^@+/, '').trim();
  return cleaned ? `https://www.instagram.com/${encodeURIComponent(cleaned)}/` : '';
};

interface ZapAnalyticsPanelProps {
  influencerId: number;
}

// Shared Popper modifiers: prevents tooltip overflow on all sides (critical on
// mobile where tooltips would clip off the right/bottom edge of the viewport).
const TOOLTIP_POPPER_PROPS = {
  modifiers: [
    { name: 'preventOverflow', options: { altAxis: true, padding: 8 } },
    { name: 'flip', options: { fallbackPlacements: ['bottom', 'top', 'left', 'right'] } },
  ],
};

// Rich hover tooltip — uses MUI <Tooltip> (instant, arrow, supports JSX) so hovers
// behave exactly like the Admin "Zap Flow" dashboard instead of the delayed/plain
// native title bubble. Renders in a portal, so it's never clipped by the panel's
// own `isolate`/`z-0` stacking context. Falls back to the child untouched when no
// title is given.
const Tip: React.FC<{ title?: React.ReactNode; placement?: 'top' | 'bottom' | 'left' | 'right'; children: React.ReactElement }> =
  ({ title, placement = 'top', children }) =>
    title ? <Tooltip title={title} arrow placement={placement} enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>{children}</Tooltip> : children;

// Small label with a hover info hint.
const InfoLabel: React.FC<{ label: string; info?: string; className?: string }> = ({ label, info, className }) => (
  <Tip title={info}>
    <span className={`inline-flex items-center gap-1 ${className || ''} ${info ? 'cursor-help' : ''}`}>
      {label}
      {info && <Info className="w-3 h-3 text-gray-300" />}
    </span>
  </Tip>
);

/* OLD — native-title version (replaced by the MUI <Tooltip>-backed one above):
const InfoLabel: React.FC<{ label: string; info?: string; className?: string }> = ({ label, info, className }) => (
  <span className={`inline-flex items-center gap-1 ${className || ''}`} title={info}>
    {label}
    {info && <Info className="w-3 h-3 text-gray-300" />}
  </span>
);
*/

// Rules-table column header with a dotted-underline affordance + hover description.
// Mirrors the Admin "Zap Flow" dashboard <ColHeader> so the rules table reads the same.
const RuleColHeader: React.FC<{ label: string; info: string; align?: 'left' | 'right' }> = ({ label, info, align = 'left' }) => (
  <th className={`px-2 py-1.5 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
    <Tip title={info}>
      <span className="inline-flex items-center gap-0.5 cursor-help border-b border-dotted border-gray-300 pb-px">
        <strong className="font-semibold">{label}</strong>
        <Info className="w-2.5 h-2.5 text-gray-300" />
      </span>
    </Tip>
  </th>
);

// ============================================================================
// MUI building blocks for the "Interaction Tracking" modal — ported from the
// Admin Zap Flow dashboard so the influencer's modal looks identical (MUI table,
// dotted column headers, clickable @handle / product / post thumbnail).
// ============================================================================
const ColHeaderMui: React.FC<{ label: string; info: string; align?: 'left' | 'right' | 'center' }> = ({ label, info, align = 'left' }) => (
  <TableCell align={align}>
    <Tooltip title={info} arrow placement="top" enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3, cursor: 'help', borderBottom: '1px dotted', borderColor: 'text.disabled', pb: '1px' }}>
        <strong className='text-[14px]'>{label}</strong>
        <Info style={{ width: 12, height: 12, color: '#bdbdbd' }} />
      </Box>
    </Tooltip>
  </TableCell>
);

const SectionHeaderMui: React.FC<{ title: string; info: string }> = ({ title, info }) => (
  <Box display="flex" alignItems="center" gap={0.5} mb={1}>
    <Typography variant="subtitle2" fontWeight={600}>{title}</Typography>
    <Tooltip title={info} arrow placement="bottom" enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>
      <Box component="span" sx={{ display: 'inline-flex', cursor: 'help', p: '4px' }}><Info style={{ width: 14, height: 14, color: '#bdbdbd' }} /></Box>
    </Tooltip>
  </Box>
);

const IgUsernameLinkMui: React.FC<{ username: string | null | undefined; fallback?: string }> = ({ username, fallback = '—' }) => {
  const url = igProfileUrl(username);
  const display = username ? `@${String(username).replace(/^@+/, '')}` : fallback;
  if (!url) return <Typography variant="body2" fontWeight={500} component="span">{display}</Typography>;
  return (
    <Tooltip title="Open Instagram profile in new tab" arrow enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>
      <a href={url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>
        <Typography variant="body2" fontWeight={500} component="span" sx={{ color: '#1976d2', cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted', textUnderlineOffset: 2, '&:hover': { textDecorationStyle: 'solid' } }}>
          {display}
        </Typography>
      </a>
    </Tooltip>
  );
};

const ProductLinkMui: React.FC<{ product: { product_name?: string | null; brand_name?: string | null; short_link_code?: string | null; product_image_url?: string | null; manual_product_image_url?: string | null; product_url?: string | null } }> = ({ product }) => {
  const href = shortLinkUrl(product.short_link_code) || product.product_url || '';
  const imgSrc = productImg(product);
  const name = product.product_name || '—';
  const content = (
    <Box display="flex" alignItems="center" gap={1}>
      {imgSrc ? <Avatar src={imgSrc} variant="rounded" sx={{ width: 32, height: 32 }} /> : <Avatar variant="rounded" sx={{ width: 32, height: 32, fontSize: 12 }}>P</Avatar>}
      <Box display="flex" alignItems="center" gap={0.5}>
        <Typography variant="body2" noWrap sx={{ maxWidth: 240, color: href ? '#1976d2' : 'inherit', textDecoration: href ? 'underline' : 'none', textDecorationStyle: 'dotted', textUnderlineOffset: 2 }}>{name}</Typography>
        {href && <ExternalLink style={{ width: 12, height: 12, color: '#bdbdbd' }} />}
      </Box>
    </Box>
  );
  return (
    <Tooltip title={href ? `${name} — click to open the product page in a new tab` : name} arrow enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>
      <Box sx={{ cursor: href ? 'pointer' : 'default' }}>
        {href ? <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>{content}</a> : content}
      </Box>
    </Tooltip>
  );
};

const PostThumbnailMui: React.FC<{ post: { post_thumbnail_url?: string | null; post_media_url?: string | null; post_gcp_url?: string | null; post_permalink?: string | null; post_media_product_type?: string | null }; size?: number }> = ({ post, size = 48 }) => {
  const src = postThumb(post);
  const permalink = post.post_permalink || '';
  const mediaType = (post.post_media_product_type || '').toUpperCase();
  if (!src && !permalink) return null;
  const badgeText = mediaType === 'REELS' ? 'REEL' : mediaType === 'FEED' ? 'POST' : mediaType === 'STORY' ? 'STORY' : (mediaType || 'POST');
  const inner = (
    <Box position="relative" sx={{ width: size, height: size, borderRadius: 1, overflow: 'hidden', background: '#f5f5f5', flexShrink: 0 }}>
      {src ? <img src={src} alt="post" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} /> : <Box display="flex" alignItems="center" justifyContent="center" sx={{ width: '100%', height: '100%', color: 'text.disabled' }}><Package style={{ width: 14, height: 14 }} /></Box>}
      <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, background: 'rgba(0,0,0,0.55)', color: '#fff', fontSize: 9, fontWeight: 600, textAlign: 'center', py: 0.2, letterSpacing: 0.3 }}>{badgeText}</Box>
    </Box>
  );
  if (permalink) return <Tooltip title={`Open ${badgeText.toLowerCase()} on Instagram`} arrow enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}><a href={permalink} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-block', textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}>{inner}</a></Tooltip>;
  return <Tooltip title={badgeText} arrow enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}><Box display="inline-block">{inner}</Box></Tooltip>;
};

export const ZapAnalyticsPanel: React.FC<ZapAnalyticsPanelProps> = ({ influencerId }) => {
  const [startDate, setStartDate] = useState(thirtyDaysAgo);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [totals, setTotals] = useState<InfluencerZapTotals>(EMPTY_TOTALS);
  const [counts, setCounts] = useState<InfluencerZapAutomationCounts>(EMPTY_COUNTS);
  const [automations, setAutomations] = useState<InfluencerZapAutomationStat[]>([]);
  const [products, setProducts] = useState<InfluencerZapProductRow[]>([]);
  const [eventTypes, setEventTypes] = useState<InfluencerZapEventTypeRow[]>([]);
  const [devices, setDevices] = useState<InfluencerZapDeviceRow[]>([]);
  const [activeTab, setActiveTab] = useState(0);  
  // Per-automation breakdown rendered INLINE as cards (the same content the Admin
  // shows in its "Automations for …" popup): name, chips, lifetime trigger count,
  // linked content, and a rules table. Each card's "View Tracking" opens the
  // Interaction Tracking modal below.
  const [autoDetails, setAutoDetails] = useState<AutoDetail[]>([]);
  const [autoDetailsLoading, setAutoDetailsLoading] = useState(false);

  // "Interaction Tracking — <name>" modal: paginated activity table + per-row expand
  // showing the auto-reply message and the catalogue sent & clicks.
  const [trackingModal, setTrackingModal] = useState<{ open: boolean; name: string; activity: InfluencerZapActivityRow[] }>({ open: false, name: '', activity: [] });
  const [activityPage, setActivityPage] = useState(0);
  const [activityRpp, setActivityRpp] = useState(10);
  const [expandedActivityId, setExpandedActivityId] = useState<number | null>(null);

  // "My Earnings" tab — attributed Zap-link sales + commission (loaded lazily on tab open).
  const [earnings, setEarnings] = useState<any>(null);
  const [earningsLoading, setEarningsLoading] = useState(false);
  const inr = (n: any) => '₹' + Math.round(Number(n) || 0).toLocaleString('en-IN');

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await instagramDMAutomationService.getMyZapAnalytics({
        influencer_id: influencerId,
        start_date: startDate,
        end_date: endDate,
      });
      if (res.status === gtUtil.Constants.SUCCESS && res?.data?.rows) {
        const data = res.data.rows;
        setTotals({ ...EMPTY_TOTALS, ...(data.totals || {}) });
        setCounts({ ...EMPTY_COUNTS, ...(data.automation_counts || {}) });
        setAutomations(data.per_automation || []);
        setProducts(data.top_products || []);
        setEventTypes(data.event_type || []);
        setDevices(data.device || []);
      } else {
        setTotals(EMPTY_TOTALS); setCounts(EMPTY_COUNTS);
        setAutomations([]); setProducts([]); setEventTypes([]); setDevices([]);
      }
    } catch {
      setError('Failed to load Zap Link stats');
    } finally {
      setLoading(false);
    }
  }, [influencerId, startDate, endDate]);

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Load earnings when the My Earnings tab is open or its date range changes.
  useEffect(() => {
    if (activeTab !== 3) return;
    let cancelled = false;
    (async () => {
      setEarningsLoading(true);
      try {
        const res = await instagramDMAutomationService.getMyZapEarnings({
          influencer_id: influencerId,
          start_date: startDate,
          end_date: endDate,
        });
        if (!cancelled) {
          setEarnings(
            res.status === gtUtil.Constants.SUCCESS && res?.data?.rows ? res.data.rows : null
          );
        }
      } catch {
        if (!cancelled) setEarnings(null);
      } finally {
        if (!cancelled) setEarningsLoading(false);
      }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [activeTab, influencerId, startDate, endDate]);

  // Whenever the automation list refreshes, load each automation's rules / linked
  // posts / activity in parallel (one call per automation) and render them inline as
  // cards. The fetched activity is reused by "View Tracking" so opening that modal
  // needs no extra request.
  useEffect(() => {
    if (!automations.length) { setAutoDetails([]); return; }
    let cancelled = false;
    setAutoDetailsLoading(true);
    Promise.all(
      automations.map(async (stat): Promise<AutoDetail> => {
        try {
          const res = await instagramDMAutomationService.getMyAutomationZapTracking({
            influencer_id: influencerId,
            ig_automation_id: stat.ig_automation_id,
            start_date: startDate,
            end_date: endDate,
          });
          if (res.status === gtUtil.Constants.SUCCESS) {
            return {
              stat,
              meta: res?.data?.rows?.automation || null,
              rules: res?.data?.rows?.rules || [],
              posts: res?.data?.rows?.posts || [],
              activity: res?.data?.rows?.zap_activity || [],
            };
          }
        } catch { /* no-op — fall through to empty detail */ }
        return { stat, meta: null, rules: [], posts: [], activity: [] };
      })
    ).then((details) => { if (!cancelled) setAutoDetails(details); })
     .finally(() => { if (!cancelled) setAutoDetailsLoading(false); });
    return () => { cancelled = true; };
    // startDate/endDate are intentionally omitted: `automations` only changes after
    // load(), which already used the current dates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [automations, influencerId]);

  const openTracking = (name: string, activity: InfluencerZapActivityRow[]) => {
    setTrackingModal({ open: true, name, activity });
    setActivityPage(0);
    setExpandedActivityId(null);
  };

  // ----- Derived funnel metrics -----
  const sendRate   = pct(totals.sent_triggers, totals.total_triggers);
  const openRate   = pct(totals.opened_triggers, totals.sent_triggers);
  const clickRate  = pct(totals.total_clicks, totals.opened_triggers);
  const overallCTR = pct(totals.total_clicks, totals.sent_triggers);

  const FUNNEL = [
    { key: 'sent_triggers',   label: 'DMs Sent',      color: '#5c6bc0' },
    { key: 'opened_triggers', label: 'DMs Opened',    color: '#26a69a' },
    { key: 'unique_clicks',   label: 'Unique Clicks', color: '#ef5350' },
    { key: 'total_clicks',    label: 'Total Clicks',  color: '#ffa726' },
  ] as const;
  const maxFunnel = Math.max(totals.sent_triggers, totals.opened_triggers, totals.total_clicks, totals.unique_clicks, 1);
  const totalDeviceClicks = devices.reduce((s, d) => s + Number(d.clicks), 0);

  const hasAnyZap = automations.length > 0 || totals.total_triggers > 0 || totals.total_clicks > 0;

  // ----- Reusable bits -----
  const StatCard: React.FC<{ label: string; value: string | number; color?: string; icon?: React.ReactNode; info?: string; bg?: string }> =
    ({ label, value, color, icon, info, bg }) => (
      <div className="flex-1 min-w-[120px] border rounded-xl p-3" style={{ background: bg || '#fff' }}>
        <div className="flex items-center gap-1.5 mb-1.5">
          {icon && <span style={{ color }}>{icon}</span>}
          <InfoLabel label={label} info={info} className="text-xs text-gray-500" />
        </div>
        <p className="text-xl lg:text-2xl font-bold" style={{ color: color || '#111' }}>{value}</p>
      </div>
    );

  const MiniCard: React.FC<{ label: string; value: string | number; color?: string; bg?: string; info?: string }> =
    ({ label, value, color, bg, info }) => (
      <div className="flex-1 min-w-[110px] border rounded-xl p-2.5" style={{ background: bg || '#fafafa' }}>
        <InfoLabel label={label} info={info} className="text-[11px] text-gray-500" />
        <p className="text-base font-bold mt-0.5" style={{ color: color || '#111' }}>{value}</p>
      </div>
    );

  const StatCardSkeleton = () => (
    <div className="flex-1 min-w-[120px] border rounded-xl p-3 animate-pulse">
      <div className="h-3 w-16 bg-gray-200 rounded mb-3" />
      <div className="h-6 w-12 bg-gray-200 rounded" />
    </div>
  );

  const SectionTitle: React.FC<{ title: string; info?: string }> = ({ title, info }) => (
    <Tip title={info}>
      <p className={`text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2 flex items-center gap-1 w-fit ${info ? 'cursor-help' : ''}`}>
        {title}{info && <Info className="w-3 h-3 text-gray-300" />}
      </p>
    </Tip>
  );

  return (
    // isolate + z-0 keeps the whole panel in its own stacking context BELOW the
    // fixed translucent site-header (z-index: 9), so dense cards/images never
    // bleed over it while scrolling.
    <>

    <Card className="border mb-6 isolate relative z-0" style={{ position: 'relative', zIndex: 0 }}>
      <CardContent className="p-3 md:p-4">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-center gap-4 mb-4">

          {/* Left */}
          <div className="flex items-start gap-3">
            {/* <span
              className="inline-flex items-center justify-center w-8 h-8 rounded-lg shrink-0"
              style={{
                background:
                  'linear-gradient(135deg, #9C52A2 0%, #e1306c 50%, #FA8E41 100%)',
              }}
            >
              {/* <Zap className="w-4 h-4 text-white fill-white" /> */}
            {/* </span>  */}
            <div className=' border transition-all border-gray-200' style={{ borderRadius: '10px',width:'50px', height:'50px', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                   <img src={zapIconActive} alt="Zap Logo" width={16} height={16} />
                </div>

            <div>
              <div className="flex items-center gap-1">
                <h3 className="text-base font-semibold">
                  Zap Link Analytics
                </h3>

                <Tip
                  title="See how your shoppable product carousels are performing."
                  placement="bottom"
                >
                  <span className="inline-flex cursor-help">
                    <Info className="w-3.5 h-3.5 text-gray-300" />
                  </span>
                </Tip>
              </div>

              <p className="text-xs text-gray-500">
                How your shoppable product carousels are performing
              </p>
            </div>
          </div>

          {/* Right */}
          <div
            className="
              lg:ml-auto
              flex
              flex-wrap
              items-center
              gap-2
              p-2
              bg-gray-50
              border
              rounded-xl
              w-full
              lg:w-auto
            "
          >
            <input
              type="date"
              value={startDate}
              // Keep the range valid: "From" can't pass "To" (or today).
              max={endDate}
              onChange={(e) => {
                const v = e.target.value;
                if (v && v <= endDate) setStartDate(v);
              }}
              className="
                flex-1
                lg:flex-none
                min-w-[140px]
                text-xs
                border
                rounded-lg
                px-2
                py-1
                bg-white
              "
            />

            <span className="text-xs text-gray-400 hidden sm:block">
              to
            </span>

            <input
              type="date"
              value={endDate}
              // "To" can't precede "From" or run past today.
              min={startDate}
              max={today}
              onChange={(e) => {
                const v = e.target.value;
                if (v && v >= startDate && v <= today) setEndDate(v);
              }}
              className="
                flex-1
                lg:flex-none
                min-w-[140px]
                text-xs
                border
                rounded-lg
                px-2
                py-1
                bg-white
              "
            />

            <button
              onClick={load}
              disabled={loading}
              className="
                flex
                items-center
                justify-center
                gap-1
                px-3
                py-1.5
                text-xs
                font-medium
                bg-black
                text-white
                rounded-lg
              "
            >
              <RefreshCw
                className={`w-3 h-3 ${
                  loading ? 'animate-spin' : ''
                }`}
              />
              Apply
            </button>

            <button
              onClick={load}
              disabled={loading}
              className="
                p-1.5
                rounded-lg
                hover:bg-gray-100
              "
              title="Refresh"
            >
              <RefreshCw
                className={`w-4 h-4 text-gray-500 ${
                  loading ? 'animate-spin' : ''
                }`}
              />
            </button>
          </div>
        </div>

        

        {error && <p className="text-xs text-red-500 mb-3">{error}</p>}
        <Box
          sx={{
            borderBottom: '1px solid #eee',
            mb: 3,
          }}
        >
          <Tabs
            value={activeTab}
            onChange={(_, v) => setActiveTab(v)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: 44,

              '& .MuiTab-root': {
                minHeight: 44,
                minWidth: 'auto',
                px: 2,
                textTransform: 'none',
                fontWeight: 600,
                fontSize: {
                  xs: '13px',
                  md: '14px',
                },
              },
            }}
          >
            <Tab label="My Zap Links" />
            <Tab label="My Zap Linked Products" />
            <Tab label="Automations" />
            {/* {influencerId === 62075 && <Tab label="My Earnings" />} */}
            <Tab label="My Earnings" />
          </Tabs>
        </Box>

        {/* ===================== Configured Automations (not date filtered) ===================== */}
        {/* {activeTab === 0 && (
          <>
        <SectionTitle title="Your Configured Automations" info="A live snapshot of your current auto-reply setup. Not affected by the date range." />
        <div className="flex flex-wrap gap-2 lg:gap-3 mb-2">
          {loading ? [...Array(3)].map((_, i) => <StatCardSkeleton key={i} />) : (
            <>
              <StatCard label="Automations" value={fmtNum(counts.total_automations)} color="#5c6bc0" icon={<LayoutGrid className="w-4 h-4" />} info="Total auto-reply setups you've created (across posts, stories, live)." />
              <StatCard label="Rules" value={fmtNum(counts.total_rules)} color="#26a69a" icon={<ListChecks className="w-4 h-4" />} info="Total keyword responses across all your setups." />
              <StatCard label="Zap Link Rules" value={fmtNum(counts.zap_link_rules)} color="#f59e0b" icon={<Zap className="w-4 h-4" />} info="Keyword responses that send a shoppable product carousel." />
            </>
          )}
        </div>
        {!loading && (
          <>
            <div className="flex flex-wrap gap-2 lg:gap-3 mb-2">
              <MiniCard label="Post" value={fmtNum(counts.post_automations)} info="Auto-replies on your posts." />
              <MiniCard label="Story" value={fmtNum(counts.story_automations)} info="Auto-replies on your stories." />
              <MiniCard label="Live" value={fmtNum(counts.live_automations)} info="Auto-replies on your live videos." />
              <MiniCard label="Auto Reply Rules" value={fmtNum(counts.auto_reply_rules)} info="Keyword responses that send plain text (no products)." />
            </div>
            <div className="flex flex-wrap gap-2 lg:gap-3 mb-4">
              <MiniCard label="Rules w/ Comment Reply" value={fmtNum(counts.rules_with_comment_reply)} info="Responses that also post a public reply on the comment." />
              <MiniCard label="Rules w/ DM Text" value={fmtNum(counts.rules_with_dm_text)} info="Responses that send a text DM." />
              <MiniCard label="Rules w/ Product Links" value={fmtNum(counts.rules_with_zap_links)} info="Zap Link responses that have at least one product attached." />
            </div>
          </>
        )}
        </>
      )} */}

        {activeTab === 0 && (
          <>
            {/* ===================== Zap Link Funnel (date filtered) ===================== */}
        <SectionTitle title="Zap Link Performance" info="How your Zap Link auto-replies performed in the selected dates." />
        {/* <div className="flex flex-wrap gap-2 lg:gap-3 mb-2">
          {loading ? [...Array(4)].map((_, i) => <StatCardSkeleton key={i} />) : (
            <>
              <StatCard label="DMs Sent" value={fmtNum(totals.sent_triggers)} color="#5c6bc0" icon={<Send className="w-4 h-4" />} info="Product carousel DMs delivered on Instagram." />
              <StatCard label="DMs Opened" value={fmtNum(totals.opened_triggers)} color="#26a69a" icon={<MailOpen className="w-4 h-4" />} info="Delivered DMs the user opened." />
              <StatCard label="Total Clicks" value={fmtNum(totals.total_clicks)} color="#ffa726" icon={<MousePointerClick className="w-4 h-4" />} info="Every product click, including repeats." />
              <StatCard label="Unique Clicks" value={fmtNum(totals.unique_clicks)} color="#ef5350" icon={<Zap className="w-4 h-4" />} info="Distinct people who clicked." />
            </>
          )}
        </div> */}
        {/* {!loading && (
          <div className="flex flex-wrap gap-2 lg:gap-3 mb-4">
            <MiniCard label="Total Triggers" value={fmtNum(totals.total_triggers)} info="Times your Zap Link auto-reply fired." />
            <MiniCard label="Pending" value={fmtNum(totals.pending_triggers)} color="#e65100" bg="#fff3e0" info="Still being processed." />
            <MiniCard label="Failed Sends" value={fmtNum(totals.failed_triggers)} color="#c62828" bg="#ffebee" info="Instagram rejected the send." />
            <MiniCard label="Products Sent" value={fmtNum(totals.total_links_sent)} color="#2e7d32" bg="#e8f5e9" info="Total products delivered across all carousels." />
          </div>
        )} */}

        {/* Conversion funnel bar */}
        {!loading && hasAnyZap && (
          <>
            {/* <SectionTitle title="Conversion Funnel" info="Each bar's width is relative to the biggest stage. '% of prev' shows drop-off between stages." /> */}
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
              {FUNNEL.map((m, i) => {
                const val = (totals as any)[m.key] || 0;
                const prev = i > 0 ? ((totals as any)[FUNNEL[i - 1].key] || 0) : 0;
                const drop = i > 0 ? pct(val, prev) : null;
                const w = Math.max((val / maxFunnel) * 100, 3);
                return (
                  <div
                    key={m.key}
                    className="
                      border
                      rounded-xl
                      p-4
                      bg-white
                      min-h-[110px]
                    "
                  >
                    
                    <div className='flex items-center'>
                      <p className="text-[18px] text-gray-600 me-auto font-semibold">{m.label}</p>
                      <p className="font-bold text-[20px]">{fmtNum(val)}</p>
                    </div>
                    {!drop && (
                      <span className="text-[10px] block" style={{minHeight:'15px'}}>&nbsp;
                      </span>
                    )}
                    {drop && (
                      <span className="text-[10px] text-gray-400 flex items-center gap-0.5">
                        <TrendingDown className="w-2.5 h-2.5" />{drop} of prev
                      </span>
                    )}
                    <div className="h-1.5 rounded-full mt-2" style={{ background: m.color, width: `${w}%` }} />
                  </div>
                );
              })}
            </div>
          </>
        )}

        {/* Conversion rates */}
        {/* {!loading && hasAnyZap && (
          <div className="flex flex-wrap gap-2 lg:gap-3 mb-4">
            <MiniCard label="Send Success Rate" value={sendRate} info="Of all triggers, how many got delivered." />
            <MiniCard label="DM Open Rate" value={openRate} info="Of delivered DMs, how many were opened." />
            <MiniCard label="Open → Click Rate" value={clickRate} info="Of opened DMs, how many led to a product click." />
            <MiniCard label="Overall CTR" value={overallCTR} info="Of delivered DMs, how many led to a click." />
          </div>
        )} */}

        {/* Empty state */}
        {!loading && !hasAnyZap && (
          <div className="text-center py-8 text-gray-500">
            <Zap className="w-10 h-10 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No Zap Link activity in this date range</p>
            <p className="text-xs">Product clicks will appear here once people start tapping your carousels</p>
          </div>
        )}

        {/* ===================== Trigger source + Device ===================== */}
        {/* {!loading && hasAnyZap && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
            <div className="border rounded-xl p-3 bg-white">
              <SectionTitle title="By Trigger Source" info="Where the auto-reply fired from. T = triggered, S = sent, O = opened." />
              {eventTypes.length === 0 ? (
                <p className="text-xs text-gray-400">No data</p>
              ) : eventTypes.map((e) => (
                <div key={e.event_type} className="flex items-center justify-between mb-1.5 last:mb-0">
                  <span className="text-sm flex items-center gap-1.5">
                    {e.event_type === 'story_replies' ? <BookOpen className="w-3.5 h-3.5 text-pink-500" />
                      : e.event_type === 'live_comments' ? <Radio className="w-3.5 h-3.5 text-orange-500" />
                      : <MessageSquare className="w-3.5 h-3.5 text-blue-500" />}
                    {EVENT_TYPE_LABEL[e.event_type] || e.event_type}
                  </span>
                  <span className="flex gap-2 text-xs text-gray-500">
                    <Tip title="Triggered"><span className="cursor-help">T: {fmtNum(e.triggers)}</span></Tip>
                    <Tip title="Sent"><span className="cursor-help">S: {fmtNum(e.sent)}</span></Tip>
                    <Tip title="Opened"><span className="cursor-help">O: {fmtNum(e.opens)}</span></Tip>
                  </span>
                </div>
              ))}
            </div>
            <div className="border rounded-xl p-3 bg-white">
              <SectionTitle title="Clicks By Device" info="What device people used when they clicked a product." />
              {devices.length === 0 ? (
                <p className="text-xs text-gray-400">No clicks yet</p>
              ) : devices.map((d) => {
                const meta = DEVICE_META[d.device_type] || DEVICE_META.unknown;
                const share = totalDeviceClicks ? ((Number(d.clicks) / totalDeviceClicks) * 100).toFixed(0) : '0';
                return (
                  <div key={d.device_type} className="flex items-center gap-2 mb-1.5 last:mb-0">
                    <span style={{ color: meta.color }}>{meta.icon}</span>
                    <span className="text-sm flex-1">{meta.label}</span>
                    <span className="text-xs text-gray-500">{fmtNum(d.clicks)} ({share}%)</span>
                  </div>
                );
              })}
            </div>
          </div>
        )} */}
        </>)}

        {activeTab === 1 && (
          <>

            {/* ===================== Top clicked products ===================== */}
        {/* {!loading && products.length > 0 && (
          <div>
            <SectionTitle title="Top Clicked Products" info="Your most-clicked products from Zap Link carousels in this range." />
            <div className="border rounded-xl overflow-hidden">
              {products.slice(0, 10).map((p, idx) => {
                const href = shortLinkUrl(p.short_link_code) || p.product_url || '';
                const img = productImg(p);
                const repeat = Number(p.clicks) > 0
                  ? (((Number(p.clicks) - Number(p.unique_clicks)) / Number(p.clicks)) * 100).toFixed(0) + '%'
                  : '—';
                return (
                  <div key={p.ig_zap_link_id}
                    className={`flex items-center gap-2.5 px-3 py-2 ${idx !== 0 ? 'border-t' : ''} bg-white`}>
                    {img ? (
                      <img src={img} alt={p.product_name || 'product'} className="w-9 h-9 rounded object-cover shrink-0" />
                    ) : (
                      <div className="w-9 h-9 rounded bg-gray-200 shrink-0 flex items-center justify-center text-gray-400 text-xs">P</div>
                    )}
                    <div className="flex-1 min-w-0">
                      {href ? (
                        <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm font-medium text-[#1976d2] hover:underline truncate block">
                          {p.product_name || '—'}
                        </a>
                      ) : (
                        <p className="text-sm font-medium truncate">{p.product_name || '—'}</p>
                      )}
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {p.brand_name && <span className="text-xs text-gray-400 truncate max-w-[160px]">{p.brand_name}</span>}
                        {p.short_link_code && (
                          <Tip title="Our tracking short link — opens the product"><a href={shortLinkUrl(p.short_link_code)} target="_blank" rel="noopener noreferrer" className="text-[10px] font-mono px-1 py-0.5 rounded-full bg-gray-50 text-gray-500 border hover:bg-gray-100">{p.short_link_code}</a></Tip>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-semibold text-orange-600">{fmtNum(p.clicks)}</p>
                      <p className="text-[11px] text-gray-400">{fmtNum(p.unique_clicks)} unique · {repeat} repeat</p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )} */}
            {!loading && products.length > 0 && (
              <div>
                <SectionTitle
                  title="Top Performing Products"
                  info="Products receiving the most clicks from your Zap Links."
                />

                <div className="divide-y rounded-xl border bg-white overflow-hidden">
                  {products.slice(0, 10).map((p, idx) => {
                    const href =
                      shortLinkUrl(p.short_link_code) || p.product_url || '';

                    const img = productImg(p);
                    const brand =
                      p.brand_name?.split('|')[0] || '';
                    return (
                      <div
                        key={p.ig_zap_link_id}
                        className="flex gap-3 px-3 py-3 hover:bg-gray-50 transition-colors"
                      >
                        {/* Rank */}
                        <div
                          className={`
                            w-8 h-8 rounded-full flex items-center justify-center
                            text-xs font-semibold shrink-0
                            ${
                              idx === 0
                                ? 'bg-yellow-100 text-yellow-700'
                                : idx === 1
                                ? 'bg-gray-100 text-gray-700'
                                : idx === 2
                                ? 'bg-orange-100 text-orange-700'
                                : 'bg-gray-50 text-gray-500'
                            }
                          `}
                        >
                          {idx + 1}
                        </div>

                        {/* Product Image */}
                        {img ? (
                          <img
                            src={img}
                            alt={p.product_name || 'product'}
                            className="
                              w-12 h-12
                              rounded-lg
                              object-cover
                              border
                              shrink-0
                            "
                          />
                        ) : (
                          <div
                            className="
                              w-12 h-12
                              rounded-lg
                              bg-gray-100
                              flex items-center justify-center
                              text-gray-400
                              shrink-0
                            "
                          >
                            P
                          </div>
                        )}

                        {/* Product Info */}
                        <div className="flex-1 min-w-0">
                          {href ? (
                            <a
                              href={href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="
                                text-sm
                                font-medium
                                text-gray-900
                                hover:text-blue-600
                                line-clamp-2
                                block
                                sm:truncate
                              "
                            >
                              {p.product_name}
                            </a>
                          ) : (
                            <p className="text-sm font-medium line-clamp-2">
                              {p.product_name}
                            </p>
                          )}

                          <p className="text-xs text-gray-500 truncate mt-0.5">
                            {brand}
                          </p>

                          {/* Mobile metrics */}
                          <div className="flex md:hidden items-center gap-2 mt-1">
                            <span className="text-xs">
                              <strong>{fmtNum(p.clicks)}</strong> clicks
                            </span>

                            <span className="text-gray-300">•</span>

                            <span className="text-xs">
                              <strong>{fmtNum(p.unique_clicks)}</strong> unique
                            </span>
                          </div>
                        </div>

                        {/* Desktop Metrics */}
                        <div className="hidden md:block text-sm font-medium text-gray-700 whitespace-nowrap">
                          <span className="font-bold">
                            {fmtNum(p.clicks)}
                          </span>

                          <span className="text-xs ml-1">
                            clicks
                          </span>

                          <span className="mx-2 text-gray-300">
                            •
                          </span>

                          <span className="font-bold">
                            {fmtNum(p.unique_clicks)}
                          </span>

                          <span className="text-xs ml-1">
                            unique
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>)}
        {activeTab === 2 && (
          <>

            {/* ===================== By Automation (inline cards — mirrors the Admin "Automations for …" popup) ===================== */}
        {!loading && automations.length > 0 && (
          <div className="mt-4 flex flex-col gap-2">
            
            {autoDetailsLoading ? (
              <div className="space-y-3">
                {[...Array(2)].map((_, i) => <div key={i} className="h-28 bg-gray-100 rounded-xl animate-pulse" />)}
              </div>
            ) : autoDetails.length === 0 ? (
              <p className="text-xs text-gray-400">No automations found.</p>
            ) : 
            // autoDetails.map((d) => {
            //   const name = d.meta?.automation_name || d.stat.automation_name || `Automation #${d.stat.ig_automation_id}`;
            //   const mediaType = d.meta?.automation_media_type || d.stat.automation_media_type;
            //   const status = d.meta?.status || d.stat.status;
            //   return (
            //     <div key={d.stat.ig_automation_id} className="border rounded-xl bg-white p-3 mb-3">
            //       <div className="flex items-start justify-between gap-2 flex-wrap">
            //         <div className="min-w-0">
            //           <p className="font-semibold text-base">{name}</p>
            //           <div className="flex items-center gap-2 flex-wrap mt-1">
            //             {mediaType && <span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-gray-50 text-gray-600 uppercase">{mediaType}</span>}
            //             <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${status === 'Live' ? 'text-green-700 border-green-300 bg-green-50' : 'text-gray-500 border-gray-200 bg-gray-50'}`}>{status}</span>
            //             {d.meta && (
            //               <Tip title="Total times this automation has fired since it was created (all-time, not date filtered)">
            //                 <span className="text-xs text-gray-500 cursor-help">Total trigger count (lifetime): <span className="font-semibold text-gray-700">{fmtNum(d.meta.trigger_count)}</span></span>
            //               </Tip>
            //             )}
            //           </div>
            //         </div>
            //         <button onClick={() => openTracking(name, d.activity)} className="flex items-center gap-1 text-xs font-medium text-[#1976d2] border border-[#1976d2]/40 rounded-lg px-2.5 py-1.5 hover:bg-blue-50 shrink-0">
            //           <ChevronRight className="w-3.5 h-3.5" /> VIEW TRACKING
            //         </button>
            //       </div>

            //       {d.posts.length > 0 && (
            //         <div className="flex items-start gap-2 flex-wrap mt-3">
            //           <span className="text-xs text-gray-500 mt-1 shrink-0">Linked content:</span>
            //           <div className="flex gap-2 flex-wrap">
            //             {d.posts.slice(0, 12).map((p) => {
            //               const src = p.gcp_media_file_url || p.thumbnail_url || p.media_url || '';
            //               const badge = (p.media_product_type || '').toUpperCase();
            //               const label = badge === 'REELS' ? 'REEL' : badge === 'FEED' ? 'POST' : badge === 'STORY' ? 'STORY' : (badge || 'POST');
            //               const inner = (
            //                 <div className="relative w-14 h-14 rounded overflow-hidden bg-gray-100 border">
            //                   {src ? <img src={src} alt="post" className="w-full h-full object-cover" /> : null}
            //                   <span className="absolute bottom-0 inset-x-0 bg-black/55 text-white text-[8px] text-center leading-3">{label}</span>
            //                 </div>
            //               );
            //               return p.permalink ? (
            //                 <a key={p.ig_automation_post_id} href={p.permalink} target="_blank" rel="noopener noreferrer">{inner}</a>
            //               ) : <div key={p.ig_automation_post_id}>{inner}</div>;
            //             })}
            //             {d.posts.length > 12 && <span className="text-xs text-gray-400 self-center">+{d.posts.length - 12} more</span>}
            //           </div>
            //         </div>
            //       )}

            //       <div className="overflow-x-auto border rounded-lg bg-white mt-3">
            //         <table className="w-full text-xs">
            //           <thead>
            //             <tr className="bg-gray-100 text-gray-500 text-left">
            //               <RuleColHeader label="Rule" info="A reference number for this keyword response (used by us internally)." />
            //               <RuleColHeader label="Type" info="What kind of reply this is: Auto Reply (plain text message and/or a public comment reply) or Zap Link (a shoppable product carousel)." />
            //               <RuleColHeader label="Status" info="Whether this keyword response is currently active. Live = active and able to fire. Deleted = removed by you (historical data still shown). Expired = inactive because the Instagram connection is broken." />
            //               <RuleColHeader label="Keywords" info="The trigger words people need to comment for this response to fire. Hover the value to see the full list if it gets cut off." />
            //               <RuleColHeader label="Triggers" align="right" info="How many times this keyword response fired in the date range — i.e. how many people commented with a matching keyword and we tried to reply." />
            //               <RuleColHeader label="Opens" align="right" info="Of those messages, how many the recipient actually opened and read." />
            //               <RuleColHeader label="Clicks" align="right" info="How many times someone clicked a product in the carousel. Only applies to Zap Link responses — shows '—' for plain text Auto Reply responses." />
            //             </tr>
            //           </thead>
            //           <tbody>
            //             {d.rules.length === 0 ? (
            //               <tr><td colSpan={7} className="text-center text-gray-400 py-3">No rules configured for this automation</td></tr>
            //             ) : d.rules.map((r) => {
            //               const isZap = r.rule_type === 'zap_link';
            //               const triggers = isZap ? Math.max(Number(r.zap_triggers) || 0, Number(r.dm_triggers) || 0) : Number(r.dm_triggers) || 0;
            //               const opens = isZap ? Math.max(Number(r.zap_opens) || 0, Number(r.dm_opens) || 0) : Number(r.dm_opens) || 0;
            //               return (
            //                 <tr key={r.ig_automation_rule_id} className={`border-t ${r.status === 'Deleted' ? 'opacity-60' : ''}`}>
            //                   <td className="px-2 py-1.5 font-mono text-gray-500">#{r.ig_automation_rule_id}</td>
            //                   <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${isZap ? 'text-orange-700 border-orange-300 bg-orange-50' : 'text-blue-700 border-blue-300 bg-blue-50'}`}>{isZap ? 'Zap Link' : 'Auto Reply'}</span></td>
            //                   <td className="px-2 py-1.5"><span className={`px-1.5 py-0.5 rounded-full border text-[10px] font-medium ${r.status === 'Live' ? 'text-green-700 border-green-300 bg-green-50' : 'text-gray-500 border-gray-200 bg-gray-50'}`}>{r.status}</span></td>
            //                   <td className="px-2 py-1.5">
            //                     {r.keywords ? (
            //                       <Tip title={
            //                         <div className="p-0.5">
            //                           <p className="font-semibold mb-0.5">All keywords ({r.keywords.split(',').length})</p>
            //                           {r.keywords.split(',').map((kw, i) => <p key={i}>• {kw.trim()}</p>)}
            //                         </div>
            //                       }>
            //                         <span className="block max-w-[160px] truncate cursor-help underline decoration-dotted underline-offset-2">{r.keywords}</span>
            //                       </Tip>
            //                     ) : <span className="block max-w-[160px] truncate">—</span>}
            //                   </td>
            //                   <td className="px-2 py-1.5 text-right">{fmtNum(triggers)}</td>
            //                   <td className="px-2 py-1.5 text-right">{fmtNum(opens)}</td>
            //                   <td className="px-2 py-1.5 text-right">{isZap ? fmtNum(r.zap_clicks) : '—'}</td>
            //                 </tr>
            //               );
            //             })}
            //           </tbody>
            //         </table>
            //       </div>
            //     </div>
            //   );
            // })}
            autoDetails.map((d) => {
              const name =
                d.meta?.automation_name ||
                d.stat.automation_name ||
                `Automation #${d.stat.ig_automation_id}`;

              const mediaType =
                d.meta?.automation_media_type ||
                d.stat.automation_media_type;

              const status =
                d.meta?.status ||
                d.stat.status;

              const totalTriggers = d.rules.reduce(
                (sum, r) =>
                  sum +
                  Math.max(
                    Number(r.zap_triggers || 0),
                    Number(r.dm_triggers || 0)
                  ),
                0
              );

              const totalOpens = d.rules.reduce(
                (sum, r) =>
                  sum +
                  Math.max(
                    Number(r.zap_opens || 0),
                    Number(r.dm_opens || 0)
                  ),
                0
              );

              const totalClicks = d.rules.reduce(
                (sum, r) => sum + Number(r.zap_clicks || 0),
                0
              );

              const thumb: any =
                d.posts?.[0]?.gcp_media_file_url ||
                d.posts?.[0]?.thumbnail_url ||
                d.posts?.[0]?.media_url;

              // Unique rule types
              const ruleTypes = [
                ...new Set(
                  d.rules.map((r) =>
                    r.rule_type === 'zap_link'
                      ? 'Zap Link'
                      : 'Auto Reply'
                  )
                ),
              ];

              // Unique keywords
              const allKeywords = [
                ...new Set(
                  d.rules.flatMap((r) =>
                    (r.keywords || '')
                      .split(',')
                      .map((k) => k.trim())
                      .filter(Boolean)
                  )
                ),
              ];

              return (
                <>
                  <div className="md:hidden border rounded-xl p-3 bg-white gap-2">
                    <div className="flex items-start gap-3">
                      <img
                        src={thumb}
                        alt=""
                        className="
                          w-12
                          h-12
                          rounded-lg
                          object-cover
                          border
                          shrink-0
                        "
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-sm truncate">
                              {name}
                            </h3>

                            <div className="flex gap-1 mt-1 flex-wrap">
                              {mediaType && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-700 text-white">
                                  {mediaType}
                                </span>
                              )}

                              <span
                                className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                  status === 'Live'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-gray-100 text-gray-500'
                                }`}
                              >
                                {status}
                              </span>
                            </div>
                          </div>

                          <button
                            onClick={() =>
                              openTracking(name, d.activity)
                            }
                            className="text-blue-600 text-sm font-medium shrink-0"
                          >
                            View →
                          </button>
                        </div>

                        {/* Metrics */}
                        <div className="mt-3 text-sm space-y-1">
                          <div>
                            <strong>{fmtNum(totalTriggers)}</strong> Triggers
                            <span className="mx-2 text-gray-300">•</span>
                            <strong>{fmtNum(totalOpens)}</strong> Opens
                          </div>

                          <div>
                            <span className="font-medium text-orange-600">
                              {fmtNum(totalClicks)} Clicks
                            </span>

                            <span className="mx-2 text-gray-300">•</span>

                            <strong>{d.rules.length}</strong> Rules
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="hidden md:block">
                    <div
                      key={d.stat.ig_automation_id}
                      className="
                        bg-white
                        border
                        rounded-xl
                        px-4
                        py-3
                        hover:border-gray-300
                        transition-all
                        mb-3
                      "
                    >
                      <div className="flex items-start gap-4">
                        {/* Thumbnail */}
                        <img
                          src={thumb || undefined}
                          alt=""
                          className="
                            w-12
                            h-12
                            rounded-lg
                            object-cover
                            border
                            shrink-0
                          "
                        />

                        {/* Content */}
                        <div className="flex-1 min-w-0">
                          {/* Row 1 */}
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <h3 className="font-semibold text-[15px] text-gray-900">
                              {name}
                            </h3>

                            {mediaType && (
                              <span className="text-[10px] uppercase bg-gray-600 text-white px-1.5 py-0.5 rounded-full">
                                {mediaType}
                              </span>
                            )}

                            <span
                              className={`text-[10px] px-1.5 py-0.5 rounded-full ${
                                status === 'Live'
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-gray-100 text-gray-500'
                              }`}
                            >
                              {status}
                            </span>
                          </div>

                          {/* Row 2 */}
                          <div className="flex flex-wrap items-center gap-2 mt-1 text-sm">
                            <span className="flex items-center gap-1">
                              <strong className='text-[14px]'>{fmtNum(totalTriggers)}</strong> <span className='text-[12px]'>Triggers</span>
                              <Tip title="Number of times this automation was triggered by matching comments or keywords during the selected date range.">
                                <Info className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tip>
                            </span>

                            <span className="text-gray-300">•</span>

                            <span className="flex items-center gap-1">
                              <strong className='text-[14px]'>{fmtNum(totalOpens)}</strong> <span className='text-[12px]'>Opens</span>
                              <Tip title="Number of users who opened the message sent by this automation.">
                                <Info className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tip>
                            </span>

                            <span className="text-gray-300">•</span>

                            <span className="flex items-center gap-1 font-medium text-orange-600">
                              {fmtNum(totalClicks)} <span className='text-[12px]'>Clicks</span>
                              <Tip title="Total product clicks generated from this automation. A user may click multiple products.">
                                <Info className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tip>
                            </span>

                            <span className="text-gray-300">•</span>

                            <span className="flex items-center gap-1">
                              {d.rules.length} <span className='text-[12px]'>Rules</span>
                              <Tip title="Keyword response rules configured under this automation, including Auto Replies and Zap Links.">
                                <Info className="w-3 h-3 text-gray-400 cursor-help" />
                              </Tip>
                            </span>
                          </div>
                          {/* Row 3 */}
                          <div className='flex items-center mt-2'>
                            {allKeywords.length > 0 && (
                              <>
                                <span className='text-[12px] me-1'>Keywords:</span>

                                <div className="flex flex-wrap gap-1">
                                  {allKeywords.slice(0, 5).map((kw) => (
                                    <span
                                      key={kw}
                                      className="
                                        px-2
                                        py-0.5
                                        text-[11px]
                                        rounded-full
                                        bg-gray-100
                                        text-gray-600
                                      "
                                    >
                                      {kw}
                                    </span>
                                  ))}

                                  {allKeywords.length > 5 && (
                                    <span className="text-[11px] text-gray-500 self-center">
                                      +{allKeywords.length - 5} more
                                    </span>
                                  )}
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Action */}
                        <button
                          onClick={() => openTracking(name, d.activity)}
                          className="
                            shrink-0
                            text-sm
                            font-medium
                            text-blue-600
                            hover:text-blue-700
                            self-center
                          "
                        >
                          View →
                        </button>
                      </div>
                    </div>
                  </div>
                </>
              );
            })}
          </div>
        )}

        {/* ============ MODAL: Interaction Tracking (mirrors Admin) ============ */}
        <Dialog open={trackingModal.open} onClose={() => setTrackingModal({ open: false, name: '', activity: [] })} maxWidth="xl" fullWidth fullScreen={isMobile}>
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight={600} sx={{ lineHeight: 1.3 }}>
                Interaction Tracking —<br />{trackingModal.name}
              </Typography>
              <Tooltip title="Every comment this setup replied to in the date range — one row per interaction. Expand a row to read the message that was sent and see the full catalogue with this user's clicks." arrow enterTouchDelay={0} leaveTouchDelay={4000} PopperProps={TOOLTIP_POPPER_PROPS}>
                <Box component="span" sx={{ display: 'inline-flex', cursor: 'help', p: '4px' }}><Info style={{ width: 16, height: 16, color: '#bdbdbd' }} /></Box>
              </Tooltip>
            </Box>
            <IconButton size="small" onClick={() => setTrackingModal({ open: false, name: '', activity: [] })}><Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>×</Box></IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <SectionHeaderMui title={`Activity — ${trackingModal.activity.length} interaction${trackingModal.activity.length === 1 ? '' : 's'}`} info="One row per user interaction. Expand a row to see the message that was sent and/or the full catalogue with this user's clicks." />
            {trackingModal.activity.length === 0 ? (
              <Typography variant="body2" color="text.secondary">No activity for this automation in the date range.</Typography>
            ) : isMobile ? (
              /* ---- MOBILE: card list ---- */
              <Box>
                {trackingModal.activity.slice(activityPage * activityRpp, activityPage * activityRpp + activityRpp).map((row) => {
                  const expanded = expandedActivityId === row.ig_zap_link_trigger_id;
                  const hasMsg = Boolean((row.comment_text && row.comment_text.trim()) || (row.reply_text && row.reply_text.trim()) || (row.message_text && row.message_text.trim()));
                  const catalogue = Array.isArray(row.catalogue) ? row.catalogue : [];
                  const clicksByLink: Record<string, InfluencerZapClickDetail[]> = {};
                  (row.clicks || []).forEach((c) => { const k = String(c.ig_zap_link_id); (clicksByLink[k] = clicksByLink[k] || []).push(c); });
                  const catIds = new Set(catalogue.map((i) => Number(i.ig_zap_link_id)));
                  const extra = Object.keys(clicksByLink).filter((id) => !catIds.has(Number(id))).map((id) => {
                    const c0 = clicksByLink[id][0];
                    return { ig_zap_link_id: Number(id), product_name: c0.product_name, brand_name: c0.brand_name, short_link_code: c0.short_link_code, product_image_url: c0.product_image_url, manual_product_image_url: c0.manual_product_image_url, product_url: c0.product_url, price: null, discount_text: null, _removed: true } as any;
                  });
                  const catalogueRows: any[] = [...catalogue, ...extra];
                  const hasExpand = hasMsg || catalogueRows.length > 0;
                  return (
                    <Box key={row.ig_zap_link_trigger_id} sx={{ border: '1px solid #e5e7eb', borderRadius: 2, mb: 1.5, overflow: 'hidden', background: '#fff' }}>
                      {/* Card header row */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5, cursor: hasExpand ? 'pointer' : 'default' }}
                        onClick={() => hasExpand && setExpandedActivityId(expanded ? null : row.ig_zap_link_trigger_id)}>
                        {postThumb(row) && <PostThumbnailMui post={row} size={40} />}
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <IgUsernameLinkMui username={row.ig_username} fallback="—" />
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            "{row.comment_text || row.trigger_keyword}"
                          </Typography>
                        </Box>
                        {hasExpand && (
                          <IconButton size="small" sx={{ ml: 'auto', flexShrink: 0 }}>
                            {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                          </IconButton>
                        )}
                      </Box>
                      {/* Metrics row */}
                      <Box sx={{ display: 'flex', gap: 1, px: 1.5, pb: 1.5, flexWrap: 'wrap' }}>
                        {row.message_text && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">DM Sent</span>
                        )}
                        {row.opened_at && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">Opened</span>
                        )}
                        <Box sx={{ display: 'flex', gap: 2, ml: 'auto', alignItems: 'center' }}>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '10px' }}>Products</Typography>
                            <Typography variant="caption" fontWeight={600}>{fmtNum(row.links_sent_count)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '10px' }}>Clicks</Typography>
                            <Typography variant="caption" fontWeight={700} sx={{ color: Number(row.click_count) > 0 ? '#f97316' : '#9ca3af' }}>{fmtNum(row.click_count)}</Typography>
                          </Box>
                          <Box sx={{ textAlign: 'right' }}>
                            <Typography variant="caption" color="text.secondary" display="block" sx={{ fontSize: '10px' }}>Date</Typography>
                            <Typography variant="caption">{fmtDateTime(row.triggered_at)}</Typography>
                          </Box>
                        </Box>
                      </Box>
                      {/* Expand section */}
                      <Collapse in={expanded} timeout="auto" unmountOnExit>
                        <Box sx={{ p: 1.5, pt: 0, borderTop: '1px solid #f3f4f6', background: '#fafafa' }}>
                          {hasMsg && (
                            <Box mb={2} mt={1.5}>
                              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>Interaction Flow</Typography>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {row.comment_text?.trim() && (
                                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                                    <MessageSquare className="w-4 h-4 text-blue-500 mt-0.5 shrink-0" />
                                    <div>
                                      <div className="text-[10px] text-blue-600 font-medium">Comment</div>
                                      <div className="text-sm break-words">{row.comment_text}</div>
                                    </div>
                                  </div>
                                )}
                                {(row.comment_text?.trim() && (row.reply_text || row.message_text)) && (
                                  <div className="flex justify-center text-gray-400 text-sm">↓</div>
                                )}
                                {row.reply_text?.trim() && (
                                  <>
                                    <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-100">
                                      <Reply className="w-4 h-4 text-cyan-600 mt-0.5 shrink-0" />
                                      <div>
                                        <div className="text-[10px] text-cyan-600 font-medium">Reply</div>
                                        <div className="text-sm break-words">{row.reply_text}</div>
                                      </div>
                                    </div>
                                    {row.message_text && <div className="flex justify-center text-gray-400 text-sm">↓</div>}
                                  </>
                                )}
                                {row.message_text?.trim() && (
                                  <div className="flex items-start gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
                                    <Send className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                                    <div>
                                      <div className="text-[10px] text-purple-600 font-medium">DM Sent</div>
                                      <div className="text-sm break-words">{row.message_text}</div>
                                    </div>
                                  </div>
                                )}
                              </Box>
                            </Box>
                          )}
                          <Typography variant="subtitle2" fontWeight={600} display="block" mb={1}>
                            Catalogue ({catalogueRows.length} product{catalogueRows.length === 1 ? '' : 's'} · {fmtNum(row.click_count)} click{Number(row.click_count) === 1 ? '' : 's'})
                          </Typography>
                          {catalogueRows.length === 0 ? (
                            <Typography variant="caption" color="text.secondary">No catalogue products found.</Typography>
                          ) : (
                            <div className="flex flex-col gap-2">
                              {catalogueRows.map((item) => {
                                const itemClicks = clicksByLink[String(item.ig_zap_link_id)] || [];
                                const clickCount = itemClicks.length;
                                const shortHref = shortLinkUrl(item.short_link_code);
                                const img = item.manual_product_image_url || item.product_image_url;
                                return (
                                  <div key={item.ig_zap_link_id} className="bg-white border rounded-xl p-3">
                                    <div className="flex gap-3">
                                      {img ? (
                                        <Link to={shortHref} target="_blank">
                                          <img src={img} alt={item.product_name} className="w-12 h-12 rounded-lg object-cover border shrink-0" />
                                        </Link>
                                      ) : (
                                        <div className="w-12 h-12 rounded-lg bg-gray-100 shrink-0" />
                                      )}
                                      <div className="flex-1 min-w-0">
                                        <Link to={shortHref} target="_blank" className="block font-medium text-sm leading-tight break-words">{item.product_name}</Link>
                                        <div className="text-xs text-gray-500 mt-0.5">{item.brand_name || 'Unknown Brand'}</div>
                                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                                          {item.price && <span className="text-sm font-medium">₹{item.price}</span>}
                                          {item.discount_text && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{item.discount_text}</span>}
                                        </div>
                                      </div>
                                      <div className="text-right shrink-0">
                                        <div className={`text-base font-bold ${clickCount > 0 ? 'text-green-600' : 'text-gray-400'}`}>{clickCount}</div>
                                        <div className="text-[10px] text-gray-500">Clicks</div>
                                      </div>
                                    </div>
                                    {shortHref && (
                                      <div className="mt-2 pt-2 border-t flex items-center gap-1.5">
                                        <span className="text-[10px] text-gray-400">Short code:</span>
                                        <a href={shortHref} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} className="text-[11px] font-mono px-2 py-1 rounded-full bg-gray-50 text-gray-600 border hover:bg-gray-100">{item.short_link_code}</a>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </Box>
                      </Collapse>
                    </Box>
                  );
                })}
                <TablePagination
                  component="div"
                  count={trackingModal.activity.length}
                  page={activityPage}
                  onPageChange={(_, p) => setActivityPage(p)}
                  rowsPerPage={activityRpp}
                  onRowsPerPageChange={(e) => { setActivityRpp(parseInt(e.target.value, 10)); setActivityPage(0); }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </Box>
            ) : (
              /* ---- DESKTOP: table ---- */
              <TableContainer sx={{ overflowX: 'auto', border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Table size="small">
                  <TableHead>
                    {/* <TableRow sx={{ background: '#f5f5f5' }}>
                      <TableCell width={36} />
                      <ColHeaderMui label="Post" info="Thumbnail of the Instagram post, reel, or story that the user commented on. Click to open it on Instagram." />
                      <ColHeaderMui label="IG User" info="The Instagram user who commented. Click their @handle to open their profile in a new tab." />
                      <ColHeaderMui label="Keyword" info="The keyword in their comment that matched and triggered the automation." />
                      <ColHeaderMui label="Event" info="Where they commented from — Comments on a post, Story Replies, or Live Comments." />
                      <ColHeaderMui label="Triggered At" info="When this comment came in and the automation fired." />
                      <ColHeaderMui label="Opened At" info="When the user opened the DM. Blank if not opened yet." />
                      <ColHeaderMui label="Auto-Reply" align="center" info="The plain-text reply outcome. 'DM Sent' = a text DM was delivered. 'Reply' = only a public comment reply, no DM. '—' = no auto-reply for this comment." />
                      <ColHeaderMui label="Zap Link" align="right" info="The product-carousel outcome: delivery status plus how many products were sent and how many this user clicked." />
                    </TableRow> */}
                    <TableRow sx={{ background: '#f8fafc' }}>
                      <TableCell width={40} />

                      <ColHeaderMui
                        label="User"
                        info="Instagram user and comment"
                      />

                      <ColHeaderMui
                        label="Status"
                        info="DM delivery and open status"
                      />

                      <ColHeaderMui
                        label="Products"
                        align="center"
                        info="Products sent to this user"
                      />

                      <ColHeaderMui
                        label="Clicks"
                        align="center"
                        info="Product clicks by this user"
                      />

                      <ColHeaderMui
                        label="Date"
                        info="Interaction date"
                      />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {trackingModal.activity.slice(activityPage * activityRpp, activityPage * activityRpp + activityRpp).map((row) => {
                      const expanded = expandedActivityId === row.ig_zap_link_trigger_id;
                      const hasMsg = Boolean((row.comment_text && row.comment_text.trim()) || (row.reply_text && row.reply_text.trim()) || (row.message_text && row.message_text.trim()));
                      const catalogue = Array.isArray(row.catalogue) ? row.catalogue : [];
                      const clicksByLink: Record<string, InfluencerZapClickDetail[]> = {};
                      (row.clicks || []).forEach((c) => { const k = String(c.ig_zap_link_id); (clicksByLink[k] = clicksByLink[k] || []).push(c); });
                      const catIds = new Set(catalogue.map((i) => Number(i.ig_zap_link_id)));
                      const extra = Object.keys(clicksByLink).filter((id) => !catIds.has(Number(id))).map((id) => {
                        const c0 = clicksByLink[id][0];
                        return { ig_zap_link_id: Number(id), product_name: c0.product_name, brand_name: c0.brand_name, short_link_code: c0.short_link_code, product_image_url: c0.product_image_url, manual_product_image_url: c0.manual_product_image_url, product_url: c0.product_url, price: null, discount_text: null, _removed: true } as any;
                      });
                      const catalogueRows: any[] = [...catalogue, ...extra];
                      const hasExpand = hasMsg || catalogueRows.length > 0;
                      return (
                        <React.Fragment key={row.ig_zap_link_trigger_id}>
                          {/* <TableRow hover>
                            <TableCell>
                              {hasExpand && (
                                <IconButton size="small" onClick={() => setExpandedActivityId(expanded ? null : row.ig_zap_link_trigger_id)}>
                                  {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                </IconButton>
                              )}
                            </TableCell>
                            <TableCell>
                              {postThumb(row) || row.post_permalink ? <PostThumbnailMui post={row} size={44} /> : <Typography variant="caption" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell>
                              <IgUsernameLinkMui username={row.ig_username} fallback="—" />
                              <Typography variant="caption" color="text.secondary" display="block">id: {row.ig_user_id}</Typography>
                            </TableCell>
                            <TableCell><Typography variant="caption">{row.trigger_keyword || '—'}</Typography></TableCell>
                            <TableCell><span className="text-[10px] px-1.5 py-0.5 rounded-full border bg-white text-gray-500">{EVENT_TYPE_LABEL[row.event_type] || row.event_type}</span></TableCell>
                            <TableCell><Typography variant="caption">{fmtDateTime(row.triggered_at)}</Typography></TableCell>
                            <TableCell><Typography variant="caption" sx={{ color: row.opened_at ? '#2e7d32' : 'text.disabled' }}>{fmtDateTime(row.opened_at)}</Typography></TableCell>
                            <TableCell align="center">
                              {row.message_text && row.message_text.trim() ? <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-green-700 border-green-300 bg-green-50 font-medium">DM Sent</span>
                                : row.reply_text && row.reply_text.trim() ? <span className="text-[10px] px-1.5 py-0.5 rounded-full border text-gray-600 border-gray-200 bg-gray-50 font-medium">Reply</span>
                                : <Typography variant="caption" color="text.disabled">—</Typography>}
                            </TableCell>
                            <TableCell align="right">
                              <Box display="flex" flexDirection="column" alignItems="flex-end" gap={0.3}>
                                <span className={`text-[10px] px-1.5 py-0.5 rounded-full border font-medium ${row.meta_api_status === 'sent' ? 'text-green-700 border-green-300 bg-green-50' : row.meta_api_status === 'failed' ? 'text-red-600 border-red-200 bg-red-50' : 'text-yellow-700 border-yellow-200 bg-yellow-50'}`}>{row.meta_api_status}</span>
                                <Typography variant="caption" color="text.secondary">{fmtNum(row.links_sent_count)} sent / {fmtNum(row.click_count)} clicks</Typography>
                              </Box>
                            </TableCell>
                          </TableRow> */}
                          <TableRow hover>
                            <TableCell>
                              {hasExpand && (
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setExpandedActivityId(
                                      expanded
                                        ? null
                                        : row.ig_zap_link_trigger_id
                                    )
                                  }
                                >
                                  {expanded ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </IconButton>
                              )}
                            </TableCell>

                            {/* USER */}
                            <TableCell>
                              <Box display="flex" gap={1.5}>
                                {postThumb(row) && (
                                  <PostThumbnailMui
                                    post={row}
                                    size={42}
                                  />
                                )}

                                <Box>
                                  <IgUsernameLinkMui
                                    username={row.ig_username}
                                    fallback="—"
                                  />

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                  >
                                    "{row.comment_text || row.trigger_keyword}"
                                  </Typography>
                                </Box>
                              </Box>
                            </TableCell>

                            {/* STATUS */}
                            <TableCell>
                              <Box display="flex" gap={0.5} flexWrap="wrap">
                                {row.message_text && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                    DM Sent
                                  </span>
                                )}

                                {row.opened_at && (
                                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200">
                                    Opened
                                  </span>
                                )}
                              </Box>
                            </TableCell>

                            {/* PRODUCTS */}
                            <TableCell align="center">
                              {fmtNum(row.links_sent_count)}
                            </TableCell>

                            {/* CLICKS */}
                            <TableCell align="center">
                              <Typography
                                fontWeight={600}
                                sx={{
                                  color:
                                    Number(row.click_count) > 0
                                      ? '#f97316'
                                      : '#9ca3af',
                                }}
                              >
                                {fmtNum(row.click_count)}
                              </Typography>
                            </TableCell>

                            {/* DATE */}
                            <TableCell>
                              <Typography variant="caption">
                                {fmtDateTime(row.triggered_at)}
                              </Typography>
                            </TableCell>
                          </TableRow>
                          <TableRow>
                            <TableCell colSpan={9} sx={{ p: 0, border: 0 }}>
                              <Collapse in={expanded} timeout="auto" unmountOnExit>
                                <Box sx={{ p: 2, background: '#fafafa' }}>
                                  {/* {hasMsg && (
                                    <Box mb={2}>
                                      <Typography variant="caption" fontWeight={600} display="block" mb={1}>Auto-Reply message</Typography>
                                      {row.comment_text && row.comment_text.trim() !== '' && (
                                        <div className="border border-blue-200 rounded-lg p-2 mb-1.5 bg-white"><div className="flex items-center gap-1 mb-0.5"><MessageSquare className="w-3 h-3 text-[#00aae5]" /><span className="text-[11px] text-[#00aae5]">Comment</span></div><p className="text-xs text-[#2b2b2b]">{row.comment_text}</p></div>
                                      )}
                                      {row.reply_text && row.reply_text.trim() !== '' && (
                                        <div className="border border-blue-200 rounded-lg p-2 mb-1.5 bg-white"><div className="flex items-center gap-1 mb-0.5"><MessageSquare className="w-3 h-3 text-[#00aae5]" /><span className="text-[11px] text-[#00aae5]">Comment Reply</span></div><p className="text-xs text-[#2b2b2b]">{row.reply_text}</p></div>
                                      )}
                                      {row.message_text && row.message_text.trim() !== '' && (
                                        <div className="border border-purple-200 rounded-lg p-2 bg-white"><div className="flex items-center gap-1 mb-0.5"><Send className="w-3 h-3 text-[#6a1b9a]" /><span className="text-[11px] text-[#6a1b9a]">DM Sent</span></div><p className="text-xs text-[#2b2b2b]">{row.message_text}</p></div>
                                      )}
                                    </Box>
                                  )} */}
                                  {hasMsg && (
                                    <Box mb={3}>
                                      <Typography
                                        variant="subtitle2"
                                        fontWeight={600}
                                        sx={{ mb: 1 }}
                                      >
                                        Interaction Flow
                                      </Typography>

                                      <div className="flex flex-wrap items-center gap-3">
                                        {row.comment_text?.trim() && (
                                          <>
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-50 border border-blue-100">
                                              <MessageSquare className="w-4 h-4 text-blue-500" />
                                              <div>
                                                <div className="text-[10px] text-blue-600 font-medium">
                                                  Comment
                                                </div>
                                                <div className="text-sm">
                                                  {row.comment_text}
                                                </div>
                                              </div>
                                            </div>

                                            {(row.reply_text || row.message_text) && (
                                              <span className="text-gray-400">→</span>
                                            )}
                                          </>
                                        )}

                                        {row.reply_text?.trim() && (
                                          <>
                                            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-cyan-50 border border-cyan-100">
                                              <Reply className="w-4 h-4 text-cyan-600" />
                                              <div>
                                                <div className="text-[10px] text-cyan-600 font-medium">
                                                  Reply
                                                </div>
                                                <div className="text-sm">
                                                  {row.reply_text}
                                                </div>
                                              </div>
                                            </div>

                                            {row.message_text && (
                                              <span className="text-gray-400">→</span>
                                            )}
                                          </>
                                        )}

                                        {row.message_text?.trim() && (
                                          <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-purple-50 border border-purple-100">
                                            <Send className="w-4 h-4 text-purple-600" />
                                            <div>
                                              <div className="text-[10px] text-purple-600 font-medium">
                                                DM Sent
                                              </div>
                                              <div className="text-sm">
                                                {row.message_text}
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </Box>
                                  )}
                                  <Typography variant="subtitle2" fontWeight={600} display="block" mb={1}>
                                    Catalogue sent &amp; clicks ({catalogueRows.length} product{catalogueRows.length === 1 ? '' : 's'} · {fmtNum(row.click_count)} click{Number(row.click_count) === 1 ? '' : 's'} by this user)
                                  </Typography>
                                  {catalogueRows.length === 0 ? (
                                    <Typography variant="caption" color="text.secondary">No catalogue products found for this send.</Typography>
                                  ) : (
                                    // <Table size="small">
                                    //   <TableHead>
                                    //     <TableRow>
                                    //       <ColHeaderMui label="Product" info="A product in the carousel that was sent to this user. Click to open it in a new tab." />
                                    //       <ColHeaderMui label="Brand" info="The brand or website where the product is sold." />
                                    //       <ColHeaderMui label="Short Code" info="Our tracking link ID. Click to open the product page in a new tab." />
                                    //       <ColHeaderMui label="Price" info="Product price / discount captured when the carousel was built." />
                                    //       <ColHeaderMui label="Clicks" align="right" info="How many times THIS user clicked this product. 0 means it was sent but not clicked." />
                                    //       {/* <ColHeaderMui label="Last Clicked At" info="The most recent time this user clicked this product. '—' if never clicked." />
                                    //       <ColHeaderMui label="Device" info="Device / OS / browser of this user's most recent click on this product." /> */}
                                    //     </TableRow>
                                    //   </TableHead>
                                    //   <TableBody>
                                    //     {catalogueRows.map((item) => {
                                    //       const itemClicks = clicksByLink[String(item.ig_zap_link_id)] || [];
                                    //       const clickCount = itemClicks.length;
                                    //       const lastClick = itemClicks[0];
                                    //       const shortHref = shortLinkUrl(item.short_link_code);
                                    //       return (
                                    //         <TableRow key={item.ig_zap_link_id} sx={{ opacity: item._removed ? 0.7 : 1 }}>
                                    //           <TableCell><ProductLinkMui product={item} />{item._removed && <Typography variant="caption" color="text.disabled" display="block">(removed from catalogue)</Typography>}</TableCell>
                                    //           <TableCell><Typography variant="caption" color="text.secondary">{item.brand_name || '—'}</Typography></TableCell>
                                    //           <TableCell>{shortHref ? <a href={shortHref} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }} onClick={(e) => e.stopPropagation()}><span className="text-[10px] font-mono px-1 py-0.5 rounded-full bg-gray-50 text-gray-500 border hover:bg-gray-100">{item.short_link_code}</span></a> : <span className="text-[10px] font-mono px-1 py-0.5 rounded-full bg-gray-50 text-gray-500 border">{item.short_link_code || '—'}</span>}</TableCell>
                                    //           <TableCell><Box display="flex" gap={0.5} alignItems="center">{item.price ? <Typography variant="caption" color="text.secondary">{item.price}</Typography> : <Typography variant="caption" color="text.disabled">—</Typography>}{item.discount_text && <span className="text-[9px] px-1 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">{item.discount_text}</span>}</Box></TableCell>
                                    //           <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ color: clickCount > 0 ? '#2e7d32' : 'text.disabled' }}>{fmtNum(clickCount)}</Typography></TableCell>
                                    //           {/* <TableCell><Typography variant="caption" sx={{ color: lastClick ? 'inherit' : 'text.disabled' }}>{lastClick ? fmtDateTime(lastClick.clicked_at) : '—'}</Typography></TableCell>
                                    //           <TableCell><Typography variant="caption" color={lastClick ? 'inherit' : 'text.disabled'}>{lastClick ? `${lastClick.device_type}${lastClick.os ? ` · ${lastClick.os}` : ''}${lastClick.browser ? ` · ${lastClick.browser}` : ''}` : '—'}</Typography></TableCell> */}
                                    //         </TableRow>
                                    //       );
                                    //     })}
                                    //   </TableBody>
                                    // </Table>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                                      {catalogueRows.map((item) => {
                                        const itemClicks =
                                          clicksByLink[String(item.ig_zap_link_id)] || [];

                                        const clickCount = itemClicks.length;

                                        const shortHref = shortLinkUrl(item.short_link_code);

                                        const img =
                                          item.manual_product_image_url ||
                                          item.product_image_url;

                                        return (
                                          <div
                                            key={item.ig_zap_link_id}
                                            className="
                                              bg-white
                                              border
                                              rounded-xl
                                              p-3
                                              hover:border-gray-300
                                              transition-all
                                            "
                                          >
                                            <div className="flex gap-3">
                                              {/* Product Image */}

                                              {img ? (
                                                <Link to={shortHref} target="_blank">
                                                  <img
                                                    src={img}
                                                    alt={item.product_name}
                                                    className="
                                                      w-14
                                                      h-14
                                                      rounded-lg
                                                      object-cover
                                                      border
                                                      shrink-0
                                                    "
                                                  />
                                                </Link>
                                              ) : (
                                                <div className="w-14 h-14 rounded-lg bg-gray-100" />
                                              )}

                                              {/* Product Info */}
                                              <div className="flex-1 min-w-0">
                                                <Link to={shortHref} target="_blank" className="block truncate font-medium text-sm">
                                                  {item.product_name}
                                                </Link>

                                                <div className="text-xs text-gray-500 truncate">
                                                  {item.brand_name || 'Unknown Brand'}
                                                </div>

                                                <div className="flex items-center gap-2 mt-2">
                                                  {item.price && (
                                                    <span className="text-sm font-medium">
                                                      ₹{item.price}
                                                    </span>
                                                  )}

                                                  {item.discount_text && (
                                                    <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-green-50 text-green-700 border border-green-200">
                                                      {item.discount_text}
                                                    </span>
                                                  )}
                                                </div>
                                              </div>

                                              {/* Clicks */}
                                              <div className="text-right shrink-0">
                                                <div
                                                  className={`text-lg font-bold ${
                                                    clickCount > 0
                                                      ? 'text-green-600'
                                                      : 'text-gray-400'
                                                  }`}
                                                >
                                                  {clickCount}
                                                </div>

                                                <div className="text-[11px] text-gray-500">
                                                  Clicks
                                                </div>
                                              </div>
                                            </div>

                                            {/* Footer */}
                                            <div className="flex items-center justify-between mt-3 pt-2 border-t">
                                              {shortHref ? (
                                                <div className="flex items-center gap-1.5">
                                                  <span className="text-[10px] text-gray-400">Short code:</span>
                                                  <a
                                                    href={shortHref}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    onClick={(e) => e.stopPropagation()}
                                                    className="
                                                      text-[11px]
                                                      font-mono
                                                      px-2
                                                      py-1
                                                      rounded-full
                                                      bg-gray-50
                                                      text-gray-600
                                                      border
                                                      hover:bg-gray-100
                                                    "
                                                  >
                                                    {item.short_link_code}
                                                  </a>
                                                </div>
                                              ) : (
                                                <span className="text-xs text-gray-400">
                                                  —
                                                </span>
                                              )}

                                              {item._removed && (
                                                <span className="text-[11px] text-red-500">
                                                  Removed
                                                </span>
                                              )}
                                            </div>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </Box>
                              </Collapse>
                            </TableCell>
                          </TableRow>
                        </React.Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
                <TablePagination
                  component="div"
                  count={trackingModal.activity.length}
                  page={activityPage}
                  onPageChange={(_, p) => setActivityPage(p)}
                  rowsPerPage={activityRpp}
                  onRowsPerPageChange={(e) => { setActivityRpp(parseInt(e.target.value, 10)); setActivityPage(0); }}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </TableContainer>
            )}
          </DialogContent>         
        </Dialog>
        </> )}
        {/* Old version gated to influencerId === 62075 with hardcoded demo data — replaced by
            the live, data-driven version below (see getMyZapEarnings → /zap-flow/influencer/earnings). */}
        {activeTab === 3 && (
          <>
            <div className="space-y-3">
              {earningsLoading && (
                <p className="text-sm text-gray-500 py-6 text-center">Loading earnings…</p>
              )}

              {!earningsLoading && (!earnings || (earnings?.totals?.orders || 0) === 0) && (
                <div className="rounded-2xl border bg-white p-8 text-center">
                  <p className="font-medium text-gray-700">No earnings yet</p>
                  <p className="text-sm text-gray-500 mt-1">
                    Commissions appear here once a follower buys through your Zap link on a
                    brand that has the ClanConnect pixel installed.
                  </p>
                </div>
              )}

              {!earningsLoading && earnings && (earnings?.totals?.orders || 0) > 0 && (
                <>
                  {/* Hero */}
                  <div className="rounded-2xl border bg-gradient-to-r from-green-50 to-emerald-50 p-6">
                    <p className="text-sm text-gray-500">
                      Total Earnings
                    </p>

                    <div className="flex items-end gap-3 mt-2">
                      <h2 className="text-4xl font-bold">
                        {inr(earnings.totals.total_earnings)}
                      </h2>

                      {earnings.totals.change_pct != null && (
                        <div className={`flex items-center gap-1 mb-1 ${earnings.totals.change_pct >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          <TrendingUp size={14} />
                          <span className="text-sm font-medium">
                            {earnings.totals.change_pct >= 0 ? '+' : ''}{earnings.totals.change_pct}%
                          </span>
                        </div>
                      )}
                    </div>

                    {earnings.totals.prev_earnings != null && (
                      <p className="text-sm text-gray-500 mt-1">
                        {inr(Math.abs((earnings.totals.total_earnings || 0) - (earnings.totals.prev_earnings || 0)))}
                        {(earnings.totals.total_earnings || 0) >= (earnings.totals.prev_earnings || 0) ? ' more than' : ' less than'} previous period
                      </p>
                    )}
                  </div>

                  {/* Simple Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="border rounded-xl p-4 bg-white">
                      <ShoppingBag size={18} className="text-blue-500" />
                      <p className="text-xs text-gray-500 mt-2">Orders</p>
                      <h3 className="text-2xl font-semibold mt-1">
                        {earnings.totals.orders || 0}
                      </h3>
                    </div>

                    <div className="border rounded-xl p-4 bg-white">
                      <IndianRupee size={18} className="text-green-500" />
                      <p className="text-xs text-gray-500 mt-2">Avg Commission</p>
                      <h3 className="text-2xl font-semibold mt-1">
                        {inr(earnings.totals.avg_commission)}
                      </h3>
                    </div>

                    <div className="border rounded-xl p-4 bg-white">
                      <Clock3 size={18} className="text-orange-500" />
                      <p className="text-xs text-gray-500 mt-2">Pending Earnings</p>
                      <h3 className="text-2xl font-semibold mt-1">
                        {inr(earnings.totals.pending_earnings)}
                      </h3>
                    </div>
                  </div>

                  {/* Top Earning Products */}
                  {(earnings.top_products || []).length > 0 && (
                    <div className="border rounded-2xl bg-white p-3">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Top Earning Products</h3>
                        <span className="text-xs text-gray-500">This period</span>
                      </div>

                      <div className="space-y-2">
                        {(earnings.top_products || []).map((item: any, index: number) => (
                          <div
                            key={index}
                            className="flex items-center justify-between rounded-xl border px-4 py-3"
                          >
                            <div className="flex items-center gap-3">
                              <span className="text-sm font-medium text-gray-400">#{index + 1}</span>
                              <span className="font-medium">{item.product_name || 'Product'}</span>
                            </div>
                            <span className="font-semibold text-green-600">{inr(item.earnings)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recent Earnings */}
                  {(earnings.recent || []).length > 0 && (
                    <div className="border rounded-2xl bg-white p-3">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold">Recent Earnings</h3>
                        <span className="text-xs text-gray-500">Latest activity</span>
                      </div>

                      <div className="space-y-3">
                        {(earnings.recent || []).map((item: any, idx: number) => (
                          <div
                            key={idx}
                            className="flex items-center justify-between border-b pb-3 last:border-0 last:pb-0"
                          >
                            <div>
                              <p className="font-medium">{item.product_name || 'Product'}</p>
                              <p className="text-xs text-gray-500">
                                {fmtDateTime(item.purchased_at)}
                                {item.status && item.status !== 'pending' ? ` · ${item.status}` : ' · pending'}
                              </p>
                            </div>
                            <span className={`font-semibold ${item.status === 'pending' ? 'text-orange-500' : 'text-green-600'}`}>
                              +{inr(item.amount)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>)}
      </CardContent>
    </Card>
    </>
  );
};

export default ZapAnalyticsPanel;

/* ============================================================================
 * OLD (focused) VERSION — kept for reference per request.
 * This was the first, lighter version of the panel: 4 funnel stat cards,
 * open-rate / click-rate / products-sent pills, per-automation drill-down, and
 * a top-products list — WITHOUT the configured-automation counts, status mini
 * cards, conversion funnel bar, conversion-rate grid, trigger-source or device
 * breakdowns. The detailed version above is a superset of this logic.
 * ----------------------------------------------------------------------------
 * Backend then returned only: { totals, per_automation, top_products }.
 *
 * Render outline of the old focused version:
 *
 *   <Card className="border mb-6">
 *     <CardContent>
 *       <Header icon + "Zap Link Performance" />
 *       <DateFilter From/To + Apply />
 *       <Funnel cards: DMs Sent / DMs Opened / Total Clicks / Unique Clicks />
 *       <Pills: Open rate {pct(opened,sent)} · Click rate {pct(clicks,sent)} ·
 *               {total_links_sent} products sent />
 *       <EmptyState if no zap />
 *       <By Automation (collapsible) -> per-user drill -> product clicks />
 *       <Top Clicked Products list (clicks + unique) />
 *     </CardContent>
 *   </Card>
 *
 * The per-automation drill-down and top-products markup are reused verbatim in
 * the detailed version above.
 * ========================================================================== */
